/**
 * Health Metrics Page
 *
 * Displays growth charts and health-related metrics for a baby.
 * Features WHO growth percentile curves with baby's data overlaid.
 */

import { useState, useMemo, useEffect } from 'react';

import { useParams, Link } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { format, differenceInMonths, parseISO } from 'date-fns';

import GrowthChart, { GrowthMeasurement } from '../components/GrowthChart';

import FeedingPatterns from '../components/FeedingPatterns';

import MedicationFrequency from '../components/MedicationFrequency';
import ECPatterns from '../components/ECPatterns';
import TummyTimeChart from '../components/TummyTimeChart';

import { analyticsApi, babyApi, eventApi } from '../services/api';

import { getBabyKey, decryptEventData, decryptBabyProfile, BabyProfileData } from '../crypto';

import type { Baby } from '../types';





type MetricType = 'weight' | 'height' | 'headCircumference';

type GenderType = 'male' | 'female';



interface DataEvent {



    id: string;



    timestamp: string;



    encryptedData: string;



}







interface PercentileCurveResponse {



    percentile: number;



    data: Array<{ age: number; value: number }>;



}







export default function HealthMetrics() {



    const { babyId } = useParams<{ babyId: string }>();







    const [selectedMetric, setSelectedMetric] = useState<MetricType>('weight');



    const [selectedGender, setSelectedGender] = useState<GenderType | null>(null);







    // Fetch and decrypt baby's profile data



    const { data: baby, isLoading: babyLoading } = useQuery({



        queryKey: ['baby', babyId, 'decrypted'],



        queryFn: async () => {



            if (!babyId) return null;



            const response = await babyApi.getById(babyId);



            const rawBaby = response.data;







            const babyKey = await getBabyKey(rawBaby.id);



            if (!babyKey) {



                throw new Error("Could not retrieve baby's encryption key.");



            }







            const decryptedData = await decryptBabyProfile(rawBaby.encryptedData, babyKey);







            return {



                ...rawBaby,



                decryptedData,



            } as Baby & { decryptedData: BabyProfileData, name: string, dateOfBirth: string };



        },



        enabled: !!babyId,



    });







    // Set gender from baby's profile



    useEffect(() => {



        if (baby?.decryptedData?.gender) {



            const gender = baby.decryptedData.gender;



            if (gender === 'male' || gender === 'female') {



                setSelectedGender(gender);



            }



        }



    }, [baby]);











    // Fetch percentile curves



    const { data: percentileCurves, isLoading: curvesLoading } = useQuery({



        queryKey: ['percentile-curves', selectedGender, selectedMetric],



        queryFn: async () => {



            if (!selectedGender) return null;



            const response = await analyticsApi.getPercentileCurves(selectedGender, selectedMetric);



            return response.data as PercentileCurveResponse[];



        },



        enabled: !!selectedGender,



    });







    // Fetch all measurement events to get actual measurements



    const { data: measurementEvents } = useQuery({



        queryKey: ['events', babyId, 'measurements'],



        queryFn: async (): Promise<DataEvent[]> => {



            if (!babyId) return [];



            const response = await eventApi.list(babyId, { type: 'WEIGHT', limit: 100 });



            return response.data?.events as DataEvent[] ?? [];



        },



        enabled: !!babyId,



    });







     // Fetch sleep events for the last 24 hours



    const { data: sleepEvents } = useQuery({



        queryKey: ['events', babyId, 'SLEEP'],



        queryFn: async (): Promise<DataEvent[]> => {



            if (!babyId) return [];



            const response = await eventApi.list(babyId, { type: 'SLEEP', limit: 100 });



            return response.data?.events as DataEvent[] ?? [];



        },



        enabled: !!babyId,



    });







    // Fetch feed events for the last 24 hours



    const { data: feedEvents } = useQuery({



        queryKey: ['events', babyId, 'FEED'],



        queryFn: async (): Promise<DataEvent[]> => {



            if (!babyId) return [];



            const response = await eventApi.list(babyId, { type: 'FEED', limit: 100 });



            return response.data?.events as DataEvent[] ?? [];



        },



        enabled: !!babyId,



    });







    // Parse and decrypt measurements



    const { data: measurements, isLoading: measurementsLoading, refetch: refetchMeasurements } = useQuery({



        queryKey: ['measurements', babyId, selectedMetric],

        queryFn: async (): Promise<GrowthMeasurement[]> => {

            if (!measurementEvents || !baby?.dateOfBirth) return [];



            const birthDate = parseISO(baby.dateOfBirth);

            const babyKey = await getBabyKey(baby.id);



            if (!babyKey) {

                console.error("Could not retrieve baby's encryption key.");

                return [];

            }



            const processedMeasurements: GrowthMeasurement[] = [];



            for (const event of measurementEvents) {

                try {

                    const decrypted = await decryptEventData(event.encryptedData, babyKey);

                    if (!decrypted) continue;



                    const eventDate = parseISO(event.timestamp);

                    const ageMonths = differenceInMonths(eventDate, birthDate);



                    let value: number | undefined;

                    let originalValue: number | undefined;
                    let originalUnit: 'kg' | 'lbs' | undefined;

                    if (selectedMetric === 'weight' && decrypted.weight !== undefined) {

                        originalValue = decrypted.weight;
                        originalUnit = decrypted.unit as 'kg' | 'lbs' | undefined;
                        value = decrypted.weight;

                        if (decrypted.unit === 'lbs') {

                            value = value * 0.453592; // lbs to kg

                        }

                    } else if (selectedMetric === 'height' && decrypted.height) {

                        value = decrypted.height as number;

                    } else if (selectedMetric === 'headCircumference' && decrypted.headCircumference) {

                        value = decrypted.headCircumference as number;

                    }



                    if (value !== undefined) {

                        processedMeasurements.push({

                            ageMonths,

                            value,

                            date: format(eventDate, 'MMM d, yyyy'),
                            originalValue,
                            originalUnit,

                        });

                    }

                } catch (e) {

                    console.error('Failed to process measurement event:', e);

                }

            }



            return processedMeasurements.sort((a, b) => a.ageMonths - b.ageMonths);

        },

                enabled: !!measurementEvents && !!baby,

            });



            // Refetch measurements when the underlying events data changes.

            // Using a stable query key means we need to explicitly trigger refetch

            // when measurementEvents is updated (e.g. after creating/editing/deleting events).

            useEffect(() => {

                if (measurementEvents && measurementEvents.length > 0) {

                    refetchMeasurements();

                }

            }, [measurementEvents?.length, measurementEvents?.[measurementEvents.length - 1]?.id, refetchMeasurements]);



            // Calculate sleep summary

            const { data: sleepSummary } = useQuery({

                queryKey: ['summary', 'sleep', sleepEvents, baby],

                queryFn: async () => {

                    if (!sleepEvents || !baby) return { totalDuration: 0 };

        

                    const babyKey = await getBabyKey(baby.id);

                    if (!babyKey) return { totalDuration: 0 };

        

                    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

                    let totalDuration = 0;

        

                    for (const event of sleepEvents) {

                        if (parseISO(event.timestamp) < twentyFourHoursAgo) continue;

                        try {

                            const decrypted = await decryptEventData(event.encryptedData, babyKey);

                            if (decrypted && typeof decrypted.duration === 'number') {

                                totalDuration += decrypted.duration;

                            }

                        } catch (e) {

                            console.error('Failed to process sleep event:', e);

                        }

                    }

                    return { totalDuration };

                },

                enabled: !!sleepEvents && !!baby,

            });

        

        

            // Calculate feed summary

            const { data: feedSummary } = useQuery({

                queryKey: ['summary', 'feed', feedEvents, baby],

                queryFn: async () => {

                    if (!feedEvents || !baby) return { totalFeeds: 0, totalVolume: 0 };

        

                    const babyKey = await getBabyKey(baby.id);

                    if (!babyKey) return { totalFeeds: 0, totalVolume: 0 };

        

                    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

                    let totalFeeds = 0;

                    let totalVolume = 0;

        

                    for (const event of feedEvents) {

                        if (parseISO(event.timestamp) < twentyFourHoursAgo) continue;

                         try {

                            const decrypted = await decryptEventData(event.encryptedData, babyKey);

                            if (decrypted) {

                                totalFeeds++;

                                if (typeof decrypted.amount === 'number') {

                                                                // Normalize volume to ml (assume ml if no unit specified)

                                                                totalVolume += decrypted.amount;

                                }

                            }

                        } catch (e) {

                            console.error('Failed to process feed event:', e);

                        }

                    }

                    return { totalFeeds, totalVolume };

                },

                enabled: !!feedEvents && !!baby,

            });

        

            // Calculate current percentile

            const currentPercentile = useMemo(() => {

                if (!measurements || measurements.length === 0 || !percentileCurves) return null;

        

                const lastMeasurement = measurements[measurements.length - 1];

        if (!lastMeasurement) return null;



        const age = lastMeasurement.ageMonths;

        const value = lastMeasurement.value;



        const curveValues = percentileCurves

            .map((curve) => {

                const point = curve.data.find((d: { age: number }) => Math.abs(d.age - age) < 0.5);

                return point ? { percentile: curve.percentile, value: point.value } : null;

            })

            .filter((v): v is { percentile: number; value: number } => v !== null)

            .sort((a, b) => a.value - b.value);



        if (curveValues.length === 0) return null;



        for (let i = 0; i < curveValues.length - 1; i++) {

            const lower = curveValues[i];

            const upper = curveValues[i + 1];

            if (value >= lower.value && value <= upper.value) {

                const ratio = (value - lower.value) / (upper.value - lower.value);

                return Math.round(lower.percentile + ratio * (upper.percentile - lower.percentile));

            }

        }



        const firstCurve = curveValues[0];

        if (value < firstCurve.value) return `<${firstCurve.percentile}`;



        const lastCurve = curveValues[curveValues.length - 1];

        if (value > lastCurve.value) return `>${lastCurve.percentile}`;



        return null;

    }, [measurements, percentileCurves]);



        const growthVelocity = useMemo(() => {



            if (!measurements || measurements.length < 2) {



                return null;



            }



    



            const last = measurements[measurements.length - 1];



            const secondLast = measurements[measurements.length - 2];



    



            const valueDiff = last.value - secondLast.value;



            const daysDiff = (last.ageMonths - secondLast.ageMonths) * (365.25 / 12);



    



            if (daysDiff <= 0) return null;



    



            const ratePerWeek = (valueDiff / daysDiff) * 7;



    



            let unit = '';



            if (selectedMetric === 'weight') {



                unit = 'g/week';



                return { rate: (ratePerWeek * 1000).toFixed(0), unit, from: secondLast.date, to: last.date };



            } else {



                unit = 'cm/week';



                return { rate: ratePerWeek.toFixed(2), unit, from: secondLast.date, to: last.date };



            }



        }, [measurements, selectedMetric]);



    



        const formatWeightDisplay = (valueKg: number, originalValue?: number, originalUnit?: 'kg' | 'lbs') => {
          if (originalUnit === 'lbs' && originalValue !== undefined) {
            const wholeLbs = Math.floor(originalValue);
            const oz = Math.round((originalValue - wholeLbs) * 16);
            return `${wholeLbs} lbs ${oz} oz (${valueKg.toFixed(2)} kg)`;
          }
          return `${valueKg.toFixed(2)} kg`;
        };


        const isLoading = babyLoading || curvesLoading || measurementsLoading;



    



        if (!babyId) {



            return (



                <div className="p-4 text-center">



                    <p className="text-gray-500 dark:text-gray-400">Please select a baby first.</p>



                    <Link to="/app" className="text-primary-600 hover:underline mt-2 inline-block">



                        Go to Dashboard



                    </Link>



                </div>



            );



        }



    



        return (



            <div className="p-4 space-y-6 pb-24">



                <div className="flex items-center justify-between">



                    <div>



                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">



                            Health Metrics



                        </h1>



                        {baby && (



                            <p className="text-gray-500 dark:text-gray-400">



                                Growth tracking for {baby.decryptedData.name}



                            </p>



                        )}



                    </div>



                    <Link



                        to="/app"



                        className="text-primary-600 hover:text-primary-700 dark:text-primary-400"



                    >



                        Back to Dashboard



                    </Link>



                </div>



    



                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">



                    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 space-y-4">



                        <div className="flex flex-wrap gap-4">



                            <div className="flex-1 min-w-[150px]">



                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">



                                    Metric



                                </label>



                                <select



                                    value={selectedMetric}



                                    onChange={(e) => setSelectedMetric(e.target.value as MetricType)}



                                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"



                                >



                                    <option value="weight">Weight</option>



                                    <option value="height">Height/Length</option>



                                    <option value="headCircumference">Head Circumference</option>



                                </select>



                            </div>



                            <div className="flex-1 min-w-[150px]">



                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">



                                    Reference Curves



                                </label>



                                <input



                                    type="text"



                                    readOnly



                                    value={selectedGender ? (selectedGender.charAt(0).toUpperCase() + selectedGender.slice(1)) : 'Loading...'}



                                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"



                                />



                            </div>



                        </div>



    



                    </div>



                    <div className="bg-white dark:bg-gray-800 rounded-xl p-4">



                        {currentPercentile !== null ? (



                            <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-3 h-full flex flex-col justify-center">



                                <p className="text-sm text-gray-600 dark:text-gray-400">Current Percentile</p>



                                <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">



                                    {currentPercentile}th



                                </p>



                                {measurements && measurements.length > 0 && (



                                    <p className="text-xs text-gray-500 dark:text-gray-500">



                                        Based on last measurement: {measurements[measurements.length - 1]?.date}



                                    </p>



                                )}



                            </div>



                        ) : (



                            <div className="text-center text-gray-500 dark:text-gray-400 h-full flex flex-col justify-center">



                                Log a measurement to see percentile.



                            </div>



                        )}



                    </div>



                </div>



    



                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">



                    <div className="bg-white dark:bg-gray-800 rounded-xl p-4">



                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Growth Velocity</h3>



                        {growthVelocity ? (



                            <div>



                                <p className="text-3xl font-bold text-green-600 dark:text-green-400">



                                    ~{growthVelocity.rate} <span className="text-lg font-medium">{growthVelocity.unit}</span>



                                </p>



                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">



                                    Calculated from {growthVelocity.from} to {growthVelocity.to}



                                </p>



                            </div>



                        ) : (



                            <p className="text-sm text-gray-500 dark:text-gray-400">



                                Log at least two measurements to calculate growth velocity.



                            </p>



                        )}



                    </div>



                    <div className="bg-white dark:bg-gray-800 rounded-xl p-4">



                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Next Steps</h3>



                         <Link



                            to={`/app/baby/${babyId}/event/add?type=WEIGHT`}



                            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"



                        >



                            Log New {selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)} Measurement



                        </Link>



                                    </div>



                                </div>



                    



                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">



                                    <div className="bg-white dark:bg-gray-800 rounded-xl p-4">



                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Sleep (Last 24h)</h3>



                                        {sleepSummary && sleepSummary.totalDuration > 0 ? (



                                            <div>



                                                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">



                                                    {Math.floor(sleepSummary.totalDuration / 60)}h {sleepSummary.totalDuration % 60}m



                                                </p>



                                                 <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">



                                                    Total sleep duration



                                                </p>



                                            </div>



                                        ) : (



                                            <p className="text-sm text-gray-500 dark:text-gray-400">



                                                Log sleep events to see summary.



                                            </p>



                                        )}



                                    </div>



                                    <div className="bg-white dark:bg-gray-800 rounded-xl p-4">



                                         <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Feeding (Last 24h)</h3>



                                        {feedSummary && feedSummary.totalFeeds > 0 ? (



                                            <div className="flex gap-4">



                                                <div>



                                                    <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">



                                                        {feedSummary.totalFeeds}



                                                    </p>



                                                    <p className="text-xs text-gray-500 dark:text-gray-500">



                                                        Total feeds



                                                    </p>



                                                </div>



                                                <div>



                                                    <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">



                                                        {feedSummary.totalVolume.toFixed(0)}<span className="text-lg">ml</span>



                                                    </p>



                                                    <p className="text-xs text-gray-500 dark:text-gray-500">



                                                        ~{(feedSummary.totalVolume / 29.5735).toFixed(1)} fl oz



                                                    </p>



                                                </div>



                                            </div>



                                        ) : (



                                             <p className="text-sm text-gray-500 dark:text-gray-400">



                                                Log feeding events to see summary.



                                            </p>



                                        )}



                                    </div>



                                </div>

                                {babyId && <FeedingPatterns babyId={babyId} />}

                {babyId && <MedicationFrequency babyId={babyId} />}

                {babyId && <ECPatterns babyId={babyId} />}

                {babyId && <TummyTimeChart babyId={babyId} />}


                {/* Lbs/oz data accuracy notice */}
                {selectedMetric === 'weight' && measurements && measurements.some(m => m.originalUnit === 'lbs') && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      ⚠️ <strong>Lbs/oz display note:</strong> Weight entries logged before the lbs+oz input update
                      may show slightly different ounce values. Editing and re-saving an entry will correct it
                      using the new lbs+oz fields.
                    </p>
                  </div>
                )}






                                {isLoading ? (



                                    <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center">



                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>



                                        <p className="mt-4 text-gray-500 dark:text-gray-400">Loading growth data...</p>



                                    </div>



                ) : (



                    <GrowthChart
                        measurements={measurements || []}
                        curves={percentileCurves || []}
                        metric={selectedMetric}
                        maxAge={36}
                        babyAgeMonths={baby?.dateOfBirth ? differenceInMonths(new Date(), parseISO(baby.dateOfBirth)) : undefined}
                    />



                )}



    



                {measurements && measurements.length > 0 && (



                    <div className="bg-white dark:bg-gray-800 rounded-xl p-4">

                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">

                        Recorded Measurements

                    </h3>

                    <div className="space-y-2">

                        {measurements.map((m: GrowthMeasurement, index: number) => (

                            <div

                                key={index}

                                className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700 last:border-0"

                            >

                                <div>

                                    <p className="font-medium text-gray-900 dark:text-white">

                                        {selectedMetric === 'weight'
                                          ? formatWeightDisplay(m.value, m.originalValue, m.originalUnit)
                                          : `${m.value.toFixed(2)} cm`}

                                    </p>

                                    <p className="text-sm text-gray-500 dark:text-gray-400">

                                        Age: {m.ageMonths.toFixed(1)} months

                                    </p>

                                </div>

                                <p className="text-sm text-gray-500 dark:text-gray-400">{m.date}</p>

                            </div>

                        ))}

                    </div>

                </div>

            )}



            {!isLoading && (!measurements || measurements.length === 0) && (

                <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center">

                    <div className="text-6xl mb-4">📊</div>

                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">

                        No Growth Data Yet

                    </h3>

                    <p className="text-gray-500 dark:text-gray-400 mb-4">

                        Start logging '{selectedMetric}' measurements to see growth charts.

                    </p>

                    <Link

                        to={`/app/baby/${babyId}/event/add?type=WEIGHT`}

                        className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"

                    >

                        Log Measurement

                    </Link>

                </div>

            )}



            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">

                <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">

                    Understanding Growth Charts

                </h4>

                <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">

                    <li>• The 50th percentile means your baby is at the median.</li>

                    <li>• What matters most is consistent growth along your baby's own curve.</li>

                    <li>• Consult your pediatrician if you notice sudden changes in growth patterns.</li>

                </ul>

            </div>

        </div>

    );

}
